import { CalendarBlankIcon, MapPinIcon, UsersIcon } from "@phosphor-icons/react";
import { handleCardCtaClick } from "../../MobileApp/webviewBridge";
import type { LessonCardData } from "./validate";
export type { LessonCardData };

// ponytail: reuses the checkout card's classes rather than cloning 60 lines of
// near-identical CSS. Same shape (image, title, icon rows, total, CTA), so a
// restyle of one should move the other. Fork the styles the day they diverge.

export function formatMoney(amount: number): string {
  return Number.isInteger(amount) ? `$${amount}` : `$${amount.toFixed(2)}`;
}

// What their checkout charges today: (rate + court fee) x pack size. The court
// fee is per lesson, so a ten-pack pays it ten times. Null when we have no real
// price — never a guess, the caller is quoted this out loud.
export function lessonTotal(data: LessonCardData): string | null {
  if (typeof data.lessonPrice !== "number") return null;
  return formatMoney((data.lessonPrice + (data.courtFee ?? 0)) * (data.lessons ?? 1));
}

interface LessonCardProps {
  data: LessonCardData;
}

export function LessonCard({ data }: LessonCardProps) {
  const total = lessonTotal(data);

  return (
    <div className="lk-checkout-card">
      {data.imageUrl && (
        <div
          className="lk-checkout-card-image"
          style={{ backgroundImage: `url(${data.imageUrl})` }}
          role="img"
          aria-label={data.coachName}
        />
      )}
      <div className="lk-checkout-card-body">
        <div className="lk-checkout-card-top">
          <div className="lk-checkout-card-sport">{data.sport.toUpperCase()}</div>
          <div className="lk-checkout-card-name">{data.coachName}</div>
        </div>
        <div className="lk-checkout-card-rows">
          <div className="lk-checkout-card-row">
            <CalendarBlankIcon size={16} weight="regular" className="lk-checkout-card-icon" />
            <span>{data.whenLabel} · {data.durationMinutes} min</span>
          </div>
          <div className="lk-checkout-card-row">
            <MapPinIcon size={16} weight="regular" className="lk-checkout-card-icon" />
            <span>{data.locationName}</span>
          </div>
          <div className="lk-checkout-card-row">
            <UsersIcon size={16} weight="regular" className="lk-checkout-card-icon" />
            <span>
              {data.participants === 1 ? "1 player" : `${data.participants} players`}
              {(data.lessons ?? 1) > 1 ? ` · ${data.lessons}-lesson pack` : ""}
              {data.courtFee ? ` · incl. ${formatMoney(data.courtFee)} court fee` : ""}
            </span>
          </div>
        </div>
        <div className="lk-checkout-card-price-row">
          <span className="lk-checkout-card-total-label">
            {(data.lessons ?? 1) > 1 ? "Charged today" : "Total"}
          </span>
          <span className="lk-checkout-card-total">{total ?? "At checkout"}</span>
        </div>
        <a
          className="lk-checkout-card-cta"
          href={data.checkoutUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => handleCardCtaClick(e, data.checkoutUrl, "checkout")}
        >
          Add payment on teachme.to
        </a>
      </div>
    </div>
  );
}
